import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Coerces form field values to the appropriate type.
 * Handles common transformations like converting string numbers to actual numbers.
 * 
 * @param name - The field name
 * @param value - The raw field value
 * @param numberFields - Array of field names that should be coerced to numbers (optional)
 * @returns The coerced value or undefined for empty values
 * 
 * @example
 * ```typescript
 * // Auto-detect number fields by name suffix
 * coerceFieldValue('manufacturerNumber', '123') // returns 123
 * 
 * // Explicit number fields list
 * coerceFieldValue('customId', '456', ['customId']) // returns 456
 * ```
 */
export function coerceFieldValue(
  name: string, 
  value: unknown, 
  numberFields?: string[]
): unknown {
  // Return undefined for null, undefined, or empty string
  if (value === null || value === undefined || value === '') return undefined
  
  // Check if this field should be converted to a number
  const shouldCoerceToNumber = numberFields 
    ? numberFields.includes(name)
    : name.toLowerCase().includes('number') // Auto-detect fields ending with "Number"
  
  if (shouldCoerceToNumber) {
    const num = Number(value)
    return Number.isNaN(num) ? value : num
  }

  return value
}

// G20 dialing codes, longest-first so longer codes match before their prefixes
// (e.g. +966 before +96, +1 last). Mirrors parsePhoneE164 in addUser/schema.ts.
const G20_DIAL_CODES = ['+966', '+961', '+44', '+86', '+91', '+62', '+39', '+81', '+52', '+55', '+61', '+33', '+49', '+82', '+27', '+90', '+54', '+7', '+1']

/**
 * Validates a phone number against the backend's user contract: the national
 * number (after the country code) must be exactly 10 digits, and the country
 * code 1-3 digits. The shared Zod schema doesn't enforce this (phone is just an
 * optional string there), so composers use this for onBlur validation to avoid a
 * backend 400 on `mobileNumber` / `countryCode`. Returns an error message or null.
 *
 * An empty/absent value is treated as valid (phone is optional).
 *
 * @param value - The phone value from PhoneInputField (E.164, e.g. "+12133734253")
 */
export function validateBackendPhone(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null

  const phone = value.trim()
  if (!/^\+[1-9]\d{6,14}$/.test(phone)) {
    return 'Enter a valid international phone number (e.g. +12133734253)'
  }

  const code = G20_DIAL_CODES.find((c) => phone.startsWith(c))
  if (!code) {
    return 'Unsupported country code'
  }

  const nationalNumber = phone.slice(code.length)
  if (!/^\d{10}$/.test(nationalNumber)) {
    return 'Phone number must be exactly 10 digits after the country code'
  }

  return null
}

/**
 * Sanitizes a resource TYPE name for the resource URI: lowercase, and only
 * lowercase letters and hyphens are allowed — everything else is stripped.
 */
export function sanitizeResourceTypeName(value: unknown): string {
  return String(value ?? '').toLowerCase().replace(/[^a-z-]/g, '')
}

/**
 * Sanitizes a resource INSTANCE name for the resource URI: lowercase, and only
 * lowercase letters, digits and hyphens are allowed — everything else is stripped.
 */
export function sanitizeResourceInstanceName(value: unknown): string {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9-]/g, '')
}

/**
 * Validates an organization code against the backend DB column
 * `orgCode: varchar("org_code", { length: 4 })` — a fixed 4-character code. The
 * request-validation schema only checks `z.string()`, so a value other than 4
 * chars passes validation but is rejected/truncated by Postgres. Composers use
 * this for onBlur + onSubmit so the DB rule is enforced before submit.
 *
 * Emptiness is left to the schema's required check (returns null here).
 *
 * @param value - The org code field value
 */
export function validateOrgCode(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim() === '') return null
  // Business rule: org code must be EXACTLY 4 characters (stricter than the backend,
  // which stores varchar(4) and would accept 1-4).
  if (value.trim().length !== 4) {
    return 'Org code must be exactly 4 characters'
  }
  return null
}
